import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import * as TeamsState from '../../store/TeamsState';
import { TokenHelper } from '../../TokenHelper';
import { people } from '@microsoft/teams-js';
import { useTranslation } from 'react-i18next';
import { Consultant } from '../../interfaces/Consultant';
import { ConsultantActionCreators } from '../../store/consultants';
import { useEditorStyles, useStackClassName } from '../Styles/Editor';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Dropdown, Field, Input, InputOnChangeData, Link, Option, Spinner, Switch, SwitchOnChangeData, Toast, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { ChangeEvent } from 'react';
import { retrieveVATInfo, VATInfo } from '../Customers/CustomersEditor';
import { DisplayCountryList, IsValidCountryCode, IsVIESSupport } from '../../interfaces/Countries';
import localeCode from 'locale-code';
import { isValidEmail } from '../Tools/Validator';

export const ConsultantsEditor = () => {
    // All data needed for edition should be loaded before calling the editor
    const teamsState = useSelector<ApplicationState, TeamsState.TeamsState>((state: ApplicationState) => state.teamsState);
    const consultantEdit = useSelector<ApplicationState, Consultant | null>((state: ApplicationState) => state.consultantsState.consultantEdit);
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Fields
    const [displayName, setDisplayName] = React.useState(consultantEdit ? consultantEdit.displayName : "");
    const [email, setEmail] = React.useState(consultantEdit ? consultantEdit.email : null);
    const [o365id, setO365id] = React.useState(consultantEdit ? consultantEdit.o365id : null);
    const [isAdmin, setIsAdmin] = React.useState<boolean>(consultantEdit ? consultantEdit.isAdmin : false);
    const [canEnterDocuments, setCanEnterDocuments] = React.useState<boolean>(consultantEdit ? consultantEdit.canEnterDocuments : false);
    const [canManageConsultantDocuments, setCanManageConsultantDocuments] = React.useState<boolean>(consultantEdit ? consultantEdit.canManageConsultantDocuments : false);
    const [isActive, setIsActive] = React.useState<boolean>(consultantEdit ? consultantEdit.isActive : false);
    const [receiveNotification, setReceiveNotification] = React.useState<boolean>(consultantEdit ? consultantEdit.receiveNotification : false);
    const [receiveInvoiceEmails, setReceiveInvoiceEmails] = React.useState<boolean>(consultantEdit ? consultantEdit.receiveInvoiceEmails : false);
    const [canEnterTimeSheet, setCanEnterTimeSheet] = React.useState<boolean>(consultantEdit ? consultantEdit.canEnterTimeSheet : false);
    const [companyName, setCompanyName] = React.useState(consultantEdit ? consultantEdit.companyName : "");
    const [vat, setVat] = React.useState(consultantEdit ? consultantEdit.vat : "");
    const [iban, setIban] = React.useState(consultantEdit ? consultantEdit.iban : "");
    const [bic, setBic] = React.useState(consultantEdit ? consultantEdit.bic : "");
    const [address, setAddress] = React.useState(consultantEdit ? consultantEdit.address : "");
    const [address2, setAddress2] = React.useState(consultantEdit ? consultantEdit.address2 : "");
    const [address3, setAddress3] = React.useState(consultantEdit ? consultantEdit.address3 : "");
    const [postalCode, setPostalCode] = React.useState(consultantEdit ? consultantEdit.postalCode : "");
    const [city, setCity] = React.useState(consultantEdit ? consultantEdit.city : "");
    const [country, setCountry] = React.useState(consultantEdit ? consultantEdit.country : "");
    const [firstName, setFirstName] = React.useState(consultantEdit ? consultantEdit.firstName : "");
    const [lastName, setLastName] = React.useState(consultantEdit ? consultantEdit.lastName : "");
    const [phoneNumber, setPhoneNumber] = React.useState(consultantEdit ? consultantEdit.phoneNumber : null);
    const [companyNumber, setCompanyNumber] = React.useState(consultantEdit ? consultantEdit.companyNumber : null);
    const [privateEmail, setPrivateEmail] = React.useState(consultantEdit ? consultantEdit.privateEmail : null);
    const [language, setLanguage] = React.useState(consultantEdit ? (consultantEdit.language ? consultantEdit.language : "fr-BE") : "fr-BE");
    const [countryCode, setCountryCode] = React.useState(consultantEdit ? consultantEdit.countryCode : null);
    const [website, setWebsite] = React.useState(consultantEdit ? consultantEdit.website : null);
    const [peppolRegisterProd, setPeppolRegisterProd] = React.useState<boolean>(consultantEdit ? consultantEdit.peppolRegisterProd : false);
    const [peppolRegisterDev, setPeppolRegisterDev] = React.useState<boolean>(consultantEdit ? consultantEdit.peppolRegisterDev : false);
    const [ownPeppolDocsReceptionTeamsNotif, setOwnPeppolDocsReceptionTeamsNotif] = React.useState<boolean>(consultantEdit ? consultantEdit.ownPeppolDocsReceptionTeamsNotif : true);
    const [ownPeppolDocsReceptionEmailNotif, setOwnPeppolDocsReceptionEmailNotif] = React.useState<boolean>(consultantEdit ? consultantEdit.ownPeppolDocsReceptionEmailNotif : false);
    const [neomyticPeppolDocsReceptionTeamsNotif, setNeomyticPeppolDocsReceptionTeamsNotif] = React.useState<boolean>(consultantEdit ? consultantEdit.neomyticPeppolDocsReceptionTeamsNotif : false);

    // Fields validation
    const [isDisplayNameValid, setDisplayNameValid] = React.useState(false);
    const [isPrivateEmailValid, setPrivateEmailValid] = React.useState(false);
    const [isLanguageValid, setLanguageValid] = React.useState(false);
    // Form validation
    const [isSubmitValid, setSubmitValid] = React.useState(false);
    const [vatTestInProgress, setVatTestInProgress] = React.useState(false);
    const [vatInfo, setVatInfo] = React.useState<VATInfo | undefined>(undefined);

    React.useEffect(() => {
        let cleanedVat = vat;
        if (cleanedVat !== null && cleanedVat !== undefined) {
            cleanedVat = cleanedVat.replace(' ', '').replace('.', '');
            if (vat !== cleanedVat) {
                setVat(cleanedVat);
            }
        }
        if ((countryCode === null || countryCode === undefined) && cleanedVat !== null && cleanedVat !== undefined && cleanedVat !== "" && cleanedVat.length >= 2) {
            const countryCodeToTest = cleanedVat.substring(0, 2);
            const countryCodeToTestRes = IsValidCountryCode(countryCodeToTest);
            if (countryCodeToTestRes) {
                setCountryCode(cleanedVat.substring(0, 2).toUpperCase());
            }
        }
    }, [countryCode, vat]);

    React.useEffect(() => {
        setDisplayNameValid(displayName !== undefined && displayName !== null && displayName.length > 0);
    }, [displayName]);
    React.useEffect(() => {
        setPrivateEmailValid(privateEmail !== undefined && privateEmail !== null && privateEmail.length > 0 && isValidEmail(privateEmail));
    }, [privateEmail]);
    React.useEffect(() => {
        setLanguageValid(language !== undefined && language !== null && language.length > 0 && localeCode.validateLanguageCode(language));
    }, [language]);
    React.useEffect(() => {
        const submitValid = (
            isDisplayNameValid
            && isPrivateEmailValid
            && isLanguageValid
        );
        //if (submitValid && consultantEdit) {
        //    // If valid, check if any change on existing item
        //    submitValid = submitValid && (
        //        displayName !== consultantEdit.displayName
        //    );
        //}
        setSubmitValid(submitValid);
    }, [displayName, isDisplayNameValid, isPrivateEmailValid, isLanguageValid ]);

    const SelectO365User = () => {
        const cursel: string[] | undefined = o365id ? [o365id] : undefined;
        people.selectPeople({
            setSelected: cursel,
            openOrgWideSearchInChatOrChannel: true,
            singleSelect: true,
            title: "Select Office 365 user"
        })
            .then((people) => {
                if (people.length > 0) {
                    setO365id(people[0].objectId);
                    if (people[0].displayName) setDisplayName(people[0].displayName);
                    if (people[0].email) setEmail(people[0].email);
                }
            })
            .catch(() => { /*Unsuccessful operation*/ });
    };

    const submit = () => {
        const c: Consultant = {
            consultantid: consultantEdit && consultantEdit.consultantid ? consultantEdit.consultantid : null,
            displayName: displayName,
            email: email,
            o365id: o365id,
            isActive: isActive,
            isAdmin: isAdmin,
            canEnterDocuments: canEnterDocuments,
            canManageConsultantDocuments: canManageConsultantDocuments,
            receiveNotification: receiveNotification,
            receiveInvoiceEmails: receiveInvoiceEmails,
            canEnterTimeSheet: canEnterTimeSheet,
            companyName: companyName,
            vat: vat,
            iban: iban,
            bic: bic,
            address: address,
            address2: address2,
            address3: address3,
            postalCode: postalCode,
            city: city,
            country: country,
            firstName: firstName,
            lastName: lastName,
            phoneNumber: phoneNumber,
            companyNumber: companyNumber,
            privateEmail: privateEmail,
            language: language,
            countryCode: countryCode,
            website: website,
            peppolRegisterProd: peppolRegisterProd,
            peppolRegisterDev: peppolRegisterDev,
            ownPeppolDocsReceptionTeamsNotif,
            ownPeppolDocsReceptionEmailNotif,
            neomyticPeppolDocsReceptionTeamsNotif            
        };

        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    const toastId = (Math.random() + 1).toString(36).substring(7);
                    dispatchToast(
                        <Toast appearance="inverted">
                            <ToastTitle>"Saving consultant..."</ToastTitle>
                        </Toast>,
                        { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                    );
                    fetch("api/Consultants/" + (consultantEdit && consultantEdit.consultantid !== null ? consultantEdit.consultantid : ""), {
                        method: consultantEdit && consultantEdit.consultantid === null ? "POST" : "PUT",  // if customerid exist, path
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        body: JSON.stringify(c),
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 200) {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle>Consulant saved</ToastTitle>
                                        </Toast>
                                    ),
                                    intent: "success",
                                    toastId,
                                    timeout: 5000,
                                });
                                return response.json() as Promise<Consultant>;
                            }
                            else {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>Consulant save failed</ToastTitle>
                                        </Toast>
                                    ),
                                    intent: "error",
                                    toastId,
                                    timeout: 10000,
                                });
                            }
                        })
                        .then(data => {
                            if (data) dispatch(ConsultantActionCreators.patchConsultantEdition(data));
                            dispatch(ConsultantActionCreators.requestConsultantEdition(null));
                        });
                }
            });
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface className={styles.DialogContentConsultant}>
            <DialogBody className={styles.fluid}>
                <DialogTitle>{consultantEdit && consultantEdit.consultantid !== null ? "Edit consultant " + consultantEdit.displayName : "Create new consultant"}</DialogTitle>
                <DialogContent>
                    <form
                        onSubmit={() => {
                            dispatch(ConsultantActionCreators.requestConsultantEdition(null));
                        }}
                        className={useStackClassName()}
                        autoComplete="off"
                    >
                        <div className={styles.onSingleLine}>
                            <div className={styles.field50}>
                                <Field label={t('FirstName')} className={styles.fluid}>
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setFirstName(data.value)}
                                        value={firstName ? firstName : ""}
                                        maxLength={50}
                                    />
                                </Field>
                            </div>
                            <div className={styles.field50}>
                                <Field label={t('LastName')} className={styles.fluid}>
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setLastName(data.value)}
                                        value={lastName ? lastName : ""}
                                        maxLength={50}
                                    />
                                </Field>
                            </div>
                        </div>
                        <div className={styles.onSingleLine}>
                            <div className={styles.field50}>
                                <Field label={t('DisplayName')} required className={styles.fluid}>
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setDisplayName(data.value)}
                                        value={displayName !== null ? displayName : ""}
                                        aria-invalid={!isDisplayNameValid}
                                        maxLength={50}
                                    />
                                </Field>
                            </div>
                            <div className={styles.field50}>
                                <Field label={t('PrivateEmail')} required className={styles.fluid}>
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setPrivateEmail(data.value)}
                                        value={privateEmail ? privateEmail : ""}
                                        maxLength={50}
                                    />
                                </Field>
                            </div>
                        </div>
                        <div className={styles.onSingleLine}>
                            <div className={styles.field70}>
                                <Field label="Authentication Email" className={styles.fluid}>
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setEmail(data.value)}
                                        value={email ? email : undefined}
                                        maxLength={100}
                                    />
                                </Field>
                            </div>
                            <div className={styles.field30}>
                                <Button
                                    onClick={() => SelectO365User()}
                                    className={styles.fluid}
                                >
                                    Select O365 user
                                </Button>
                            </div>
                        </div>
                        <div className={styles.onSingleLine}>
                            <div className={styles.field50}>
                                <Switch label="is Active"
                                    checked={isActive}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setIsActive(data.checked); }} />
                                <Switch label="is Admin"
                                    checked={isAdmin}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setIsAdmin(data.checked); }} />
                                <Switch label="Receive Notification when TS changes"
                                    checked={receiveNotification}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setReceiveNotification(data.checked); }} />
                                <Switch label="Receive Invoice Emails"
                                    checked={receiveInvoiceEmails}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setReceiveInvoiceEmails(data.checked); }} />
                                <Switch label="Can manage consultant documents"
                                    checked={canManageConsultantDocuments}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setCanManageConsultantDocuments(data.checked); }} />
                                <Switch label="Teams Notification on Neomytic Peppol reception"
                                    checked={neomyticPeppolDocsReceptionTeamsNotif}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setNeomyticPeppolDocsReceptionTeamsNotif(data.checked); }} />
                            </div>
                            <div className={styles.field50}>
                                <Switch label="Can Enter TimeSheet"
                                    checked={canEnterTimeSheet}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setCanEnterTimeSheet(data.checked); }} />
                                <Switch label="Can Enter Documents"
                                    checked={canEnterDocuments}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setCanEnterDocuments(data.checked); }} />
                                <Switch label="Peppol Register in Prod"
                                    checked={peppolRegisterProd}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setPeppolRegisterProd(data.checked); }} />
                                <Switch label="Peppol Register in Dev"
                                    checked={peppolRegisterDev}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setPeppolRegisterDev(data.checked); }} />
                                <Switch label="Teams Notification on Peppol reception"
                                    checked={ownPeppolDocsReceptionTeamsNotif}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setOwnPeppolDocsReceptionTeamsNotif(data.checked); }} />
                                <Switch label="Email Notification on Peppol reception"
                                    checked={ownPeppolDocsReceptionEmailNotif}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setOwnPeppolDocsReceptionEmailNotif(data.checked); }} />
                            </div>
                        </div>
                        <div className={styles.onSingleLine}>
                            <div className={styles.field50}>
                                <Field label={t('CompanyName')} className={styles.fluid}>
                            <Input
                                autoComplete="off"
                                className={styles.fluid}
                                onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCompanyName(data.value)}
                                value={companyName ? companyName : undefined}
                                maxLength={50}
                            />
                        </Field>
                            </div>
                            <div className={styles.field50}>
                                <Field label={t('PhoneNumber')} className={styles.fluid}>
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setPhoneNumber(data.value)}
                                        value={phoneNumber ? phoneNumber : undefined}
                                        maxLength={50}
                                    />
                                </Field>
                            </div>
                        </div>
                        <div className={styles.onSingleLine}>
                            <div className={styles.field20}>
                                <Field
                                    required
                                    label={t('CountryCode')}
                                    className={styles.fluid}
                                >
                                    <Dropdown
                                        className={styles.fluid}
                                        size="medium"
                                        value={countryCode ? countryCode.toUpperCase() : ""}
                                        onOptionSelect={(event, data) => {
                                            setCountryCode(data.optionValue ?? "be");
                                        }}
                                    >
                                        {
                                            DisplayCountryList("be").map((i) =>
                                                <Option text={i.displayName} value={i.alpha2}>
                                                    {i.displayName}
                                                </Option>
                                            )
                                        }
                                    </Dropdown>
                                    {/*error={!isVatValid}*/}
                                </Field>
                            </div>
                            <div className={styles.field40}>
                                <Field
                                    required
                                    label={t('VAT')}
                                    className={styles.fluid}
                                    validationMessage={
                                        vatInfo
                                            ?
                                            vatInfo.isValid
                                                ?
                                                "Valid"
                                                :
                                                vatInfo.userError === "INVALID"
                                                    ?
                                                    "error"
                                                    :
                                                    vatInfo.userError === "MS_MAX_CONCURRENT_REQ"
                                                        ?
                                                        t('ServerBusy')
                                                        :
                                                        "warning"
                                            :
                                            null
                                    }
                                    validationState={
                                        vatInfo
                                            ?
                                            vatInfo.isValid
                                                ?
                                                "success"
                                                :
                                                vatInfo.userError === "INVALID"
                                                    ?
                                                    "error"
                                                    :
                                                    "warning"
                                            :
                                            "none"
                                    }>
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setVat(data.value)}
                                        value={vat ? vat : ""}
                                        maxLength={50}
                                    />
                                    {/*error={!isVatValid}*/}
                                </Field>
                            </div>
                            <div className={styles.field30}>
                                {vatTestInProgress
                                    ?
                                    <Spinner size="extra-tiny" />
                                    :
                                    <Button
                                        className={styles.fluid}
                                        onClick={() => {
                                            setVatTestInProgress(true);
                                            TokenHelper.GetToken(instance, teamsState)
                                                .then(token => {
                                                    if (countryCode && vat) {
                                                        retrieveVATInfo(token, countryCode, vat)
                                                            .then((r) => {
                                                                if (r) {
                                                                    setVatInfo(r);
                                                                    if (r.isValid && r.vatNumber) {
                                                                        setVat(countryCode + r.vatNumber);
                                                                    }
                                                                }
                                                                setVatTestInProgress(false);
                                                            })
                                                            .catch(() => {
                                                                setVatTestInProgress(false);
                                                            });
                                                    }
                                                    else {
                                                        setVatTestInProgress(false);
                                                    }
                                                })
                                                .catch(() => {
                                                    setVatTestInProgress(false);
                                                });
                                        }}
                                        type="submit"
                                        appearance="primary"
                                        disabled={!countryCode || !vat || vatTestInProgress || !IsVIESSupport(countryCode)}
                                    >
                                        Test
                                    </Button>
                                }

                            </div>
                        </div>
                        {vatInfo && vatInfo.isValid &&
                            <div>
                                {t('Name') + " : " + vatInfo.name + " (" + vatInfo.address + ")"}
                            </div>
                        }
                        <div className={styles.onSingleLine}>
                            <div className={styles.field20}>
                                <Field label="Language"
                                    required
                                    className={styles.fluid}
                                    validationMessage={
                                        language && localeCode.validateLanguageCode(language)
                                            ?
                                            "Valid"
                                            :
                                            "error"
                                    }
                                    validationState={
                                        language && localeCode.validateLanguageCode(language)
                                            ?
                                            "success"
                                            :
                                            "error"
                                    }
                                >
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setLanguage(data.value)}
                                        value={language ? language : ""}
                                        maxLength={5}
                                    />
                                </Field>
                            </div>
                            <div className={styles.field30}>
                                <Field label={t('CompanyNumber')} className={styles.fluid}>
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCompanyNumber(data.value)}
                                        value={companyNumber ? companyNumber : ""}
                                        maxLength={50}
                                    />
                                </Field>
                            </div>
                            <div className={styles.field20}>
                                <Field label="BIC" className={styles.fluid}>
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setBic(data.value)}
                                        value={bic ? bic : ""}
                                        maxLength={50}
                                    />
                                </Field>
                            </div>
                            <div className={styles.field30}>
                                <Field label="IBAN" className={styles.fluid}>
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setIban(data.value)}
                                        value={iban ? iban : ""}
                                        maxLength={50}
                                    />
                                </Field>
                            </div>
                        </div>
                        <Field label={t('Address')}>
                            <Input
                                autoComplete="off"
                                className={styles.fluidWithZeroBottomMargin}
                                onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setAddress(data.value)}
                                value={address ? address : ""}
                            />
                            <Input
                                autoComplete="off"
                                className={styles.fluidWithZeroBottomMargin}
                                onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setAddress2(data.value)}
                                value={address2 ? address2 : ""}
                            />
                            <Input
                                autoComplete="off"
                                className={styles.fluid}
                                onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setAddress3(data.value)}
                                value={address3 ? address3 : ""}
                            />
                        </Field>
                        <div className={styles.onSingleLine}>
                            <div className={styles.field50}>
                                <Field label={t('postalCode')} className={styles.fluid}>
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setPostalCode(data.value)}
                                        value={postalCode ? postalCode : ""}
                                        maxLength={50}
                                    />
                                </Field>
                            </div>
                            <div className={styles.field50}>
                                <Field label={t('City')} className={styles.fluid}>
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCity(data.value)}
                                        value={city ? city : ""}
                                        maxLength={50}
                                    />
                                </Field>
                            </div>
                        </div>
                        <Field label={t('Country')}>
                            <Input
                                autoComplete="off"
                                className={styles.fluid}
                                onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCountry(data.value)}
                                value={country ? country : ""}
                                maxLength={50}
                            />
                        </Field>
                        <Field label={t('website')}>
                            <Input
                                autoComplete="off"
                                className={styles.fluid}
                                onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setWebsite(data.value)}
                                value={website ? website : ""}
                                maxLength={50}
                            />
                        </Field>
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(ConsultantActionCreators.requestConsultantEdition(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        appearance="primary"
                        disabled={!isSubmitValid}
                    >
                        {t("Save")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface>
    </Dialog>;
};